import { transSericeApolloClient } from '@/graphql/client'
import { GET_EVENTS, GET_TASKS } from '@/graphql/queries/my-calendar'
import store from '@/store'

/**
 * Fetch appointments for a specific patient from calendar
 * @param {string} patientId - Patient ID
 * @param {string} startTime - Start time in ISO 8601 format (optional, defaults to current time)
 * @param {string} endTime - End time in ISO 8601 format (optional, defaults to 2 months from now)
 * @returns {Promise} Array of appointment events
 */
export async function getPatientAppointmentsApi(patientId, startTime = null, endTime = null) {
  try {
    // Get creator calendar ID from store
    let calendarId = store.state.calendar.creatorCalendarId

    // If no calendar ID in store, we need to get it first
    if (!calendarId) {
      console.warn('No calendar ID found in store, please ensure getOrCreateCalendar is called first')
      return []
    }

    // Check if this is the special clinic with multiple calendar IDs
    const userInfo = store.state.login.userInfo || {}
    const isClinic150105 = userInfo.clinic_id === 150105

    // For clinic 150105, use hardcoded calendar IDs; otherwise use the creator's calendar
    const calendarIds = isClinic150105
      ? [30790, 30791, 30789, 30788, 30787, 30793, 30792, 30794, 33843, 30795]
      : [calendarId]

    // Default time range: from now to 2 months in the future
    const now = new Date()
    const defaultStartTime = now
    const defaultEndTime = new Date(now)
    defaultEndTime.setMonth(defaultEndTime.getMonth() + 2)

    const getEventsInput = {
      calendar_ids: calendarIds,
      start_time: startTime || defaultStartTime.toISOString(),
      end_time: endTime || defaultEndTime.toISOString()
    }

    const { data } = await transSericeApolloClient.query({
      query: GET_EVENTS,
      variables: { input: getEventsInput },
      fetchPolicy: 'network-only'
    })

    // Filter events for the specific patient
    const allEvents = data.getEvents || []
    const patientEvents = allEvents.filter(event =>
      event.patient_ids && event.patient_ids.includes(Number(patientId)) &&
      !event.is_canceled &&
      event.event_type !== 'task'
    )

    return patientEvents
  } catch (error) {
    console.error('Error fetching patient appointments:', error)
    throw error
  }
}

/**
 * Fetch tasks for a specific patient from calendar
 * @param {string} patientId - Patient ID
 * @returns {Promise} Array of task events
 */
export async function getPatientTasksApi(patientId) {
  try {
    const { data } = await transSericeApolloClient.query({
      query: GET_TASKS,
      fetchPolicy: 'network-only'
    })

    // Get patient's calendar ID from the mapping in store
    const patientCalendarIdMap = store.state.myPatients.patientInfo.patientCalendarIdMap || {}
    const patientCalendarId = patientCalendarIdMap[String(patientId)]

    // Filter tasks for the specific patient using their calendar ID
    // Also filter out inactive tasks (is_active === false)
    const allTasks = data.tasksByCreator || []
    const patientTasks = allTasks.filter(task =>
      patientCalendarId && 
      String(task.calendar_id) === String(patientCalendarId) &&
      task.is_active !== false
    )

    return patientTasks
  } catch (error) {
    console.error('Error fetching patient tasks:', error)
    throw error
  }
}
